import { FormEvent, useEffect, useState } from 'react';
import type { MeResponse, OutletRef, WarehouseInput, WarehousePurpose, WarehouseRow } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatQty } from '../format';
import { DataTable, PageHeader, type Column } from '../components/ListPage';

// Mal və anbar → Anbarlar (KT-93; tənzimləmə yalnız settings.users ilə): kod, ad, obyekt, ünvan, telefon, təyinat, status, ✎. Şirkətdə bir «Əsas anbar»:
// adını sahib özü qoyur, silinmir və deaktiv olunmur; əsası dəyişmək — başqasını «Əsas et». Əlavə anbar Standart/Tam
// paketdədir (server yoxlayır). Qalığı olan anbar deaktiv olunmur — əvvəl yerdəyişmə. Kassanın satış anbarı burada seçilir.

const PURPOSE: Record<WarehousePurpose, string> = { sale: 'Satış nöqtəsi', storage: 'Anbar', both: 'Hər ikisi' };

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

export function Warehouses({ me, canTransfer, onTransfer }: { me: MeResponse; canTransfer: boolean; onTransfer: () => void }) {
  const [rows, setRows] = useState<WarehouseRow[] | null>(null);
  const [outlets, setOutlets] = useState<OutletRef[]>([]);
  const [form, setForm] = useState<{ id: string | null; v: WarehouseInput } | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .warehouses()
      .then(setRows)
      .catch((err) => setMsg({ ok: false, text: errText(err) }));
  useEffect(() => {
    void load();
    api.outlets().then(setOutlets).catch(() => setOutlets(me.outlet ? [me.outlet] : []));
  }, []);

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setMsg(null);
    try {
      setMsg({ ok: true, text: await fn() });
      setForm(null);
      await load();
    } catch (err) {
      setMsg({ ok: false, text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  function save(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    const v = { ...form.v, code: form.v.code || null, address: form.v.address || null, phone: form.v.phone || null };
    void run(async () => {
      if (form.id) await api.warehouseUpdate(form.id, v);
      else await api.warehouseCreate(v);
      return 'Saxlanıldı.';
    });
  }

  const edit = (w: WarehouseRow) =>
    setForm({ id: w.id, v: { name: w.name, code: w.code, outletId: w.outletId, address: w.address, phone: w.phone, purpose: w.purpose, twoStep: w.twoStep, active: w.active } });
  const set = <K extends keyof WarehouseInput>(k: K, val: WarehouseInput[K]) => setForm((f) => (f ? { ...f, v: { ...f.v, [k]: val } } : f));
  const editing = form?.id ? rows?.find((w) => w.id === form.id) : undefined;

  const columns: Column<WarehouseRow>[] = [
    { key: 'code', label: 'Kod', minor: true, mono: true, render: (w) => w.code ?? '—' },
    {
      key: 'name',
      label: 'Ad',
      render: (w) => (
        <>
          {w.name} {w.isMain && <span className="badge badge-ok">Əsas</span>}
          {w.registers.length > 0 && <div className="muted small">Kassa: {w.registers.map((r) => r.name).join(', ')}</div>}
        </>
      ),
    },
    { key: 'outlet', label: 'Obyekt', minor: true, render: (w) => w.outletName },
    { key: 'addr', label: 'Ünvan', minor: true, render: (w) => [w.address, w.phone].filter(Boolean).join(' · ') || '—' },
    { key: 'purpose', label: 'Təyinat', render: (w) => PURPOSE[w.purpose] + (w.twoStep ? ' · iki addımlı' : '') },
    { key: 'qty', label: 'Qalıq', align: 'right', mono: true, minor: true, render: (w) => formatQty(w.stockQty) },
    { key: 'st', label: 'Status', render: (w) => (w.active ? <span className="badge badge-ok">Aktiv</span> : <span className="badge badge-off">Deaktiv</span>) },
  ];

  return (
    <main className="page page-list">
      <PageHeader
        title="Anbarlar"
        actions={
          !form && (
            <button
              type="button"
              className="btn-primary btn-small"
              onClick={() => setForm({ id: null, v: { name: '', code: null, outletId: me.outlet?.id ?? outlets[0]?.id ?? '', purpose: 'storage', twoStep: false, active: true } })}
            >
              + Yeni anbar
            </button>
          )
        }
      />
      <p className="muted small">«Əsas anbar» şirkətdə birdir: adını dəyişə bilərsiniz, silinmir. Əlavə anbar və yerdəyişmə Standart və Tam paketdədir.</p>
      {msg && (
        <p className={msg.ok ? 'ok' : 'bad'} role="status">
          {msg.text}
          {!msg.ok && /qalıq var/.test(msg.text) && canTransfer && (
            <>
              {' '}
              <button type="button" className="link-btn" onClick={onTransfer}>
                Yerdəyişməyə keç
              </button>
            </>
          )}
        </p>
      )}
      {form && (
        <form className="card form-stack" onSubmit={save} noValidate aria-label={form.id ? 'Anbarı dəyiş' : 'Yeni anbar'}>
          <h2>{form.id ? 'Anbarı dəyiş' : 'Yeni anbar'}</h2>
          <div className="form-grid">
            <label>
              Ad
              <input value={form.v.name} maxLength={120} onChange={(e) => set('name', e.target.value)} disabled={busy} />
            </label>
            <label>
              Kod
              <input value={form.v.code ?? ''} maxLength={20} onChange={(e) => set('code', e.target.value)} disabled={busy} />
            </label>
            <label>
              Obyekt
              <select value={form.v.outletId} onChange={(e) => set('outletId', e.target.value)} disabled={busy || !!form.id}>
                {outlets.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </label>
            <label>
              Təyinat
              <select value={form.v.purpose} onChange={(e) => set('purpose', e.target.value as WarehousePurpose)} disabled={busy}>
                {(Object.keys(PURPOSE) as WarehousePurpose[]).map((k) => (
                  <option key={k} value={k}>{PURPOSE[k]}</option>
                ))}
              </select>
            </label>
            <label>
              Ünvan
              <input value={form.v.address ?? ''} maxLength={300} onChange={(e) => set('address', e.target.value)} disabled={busy} />
            </label>
            <label>
              Telefon
              <input value={form.v.phone ?? ''} maxLength={30} onChange={(e) => set('phone', e.target.value)} disabled={busy} />
            </label>
          </div>
          <label className="check">
            <input type="checkbox" checked={form.v.twoStep} onChange={(e) => set('twoStep', e.target.checked)} disabled={busy} /> İki addımlı rejim (satış → rezerv, verilmə → çıxış)
          </label>
          <label className="check">
            <input type="checkbox" checked={form.v.active} onChange={(e) => set('active', e.target.checked)} disabled={busy || !!editing?.isMain} /> Aktiv
            {editing?.isMain && <span className="muted small"> — əsas anbar deaktiv olunmur</span>}
          </label>
          <div className="form-actions">
            <button type="submit" className="btn-primary btn-small" disabled={busy || !form.v.name.trim() || !form.v.outletId}>
              Saxla
            </button>
            {editing && !editing.isMain && editing.active && (
              <button type="button" className="btn-small" disabled={busy} onClick={() => run(async () => (await api.warehouseSetMain(editing.id), `«${editing.name}» əsas anbar oldu.`))}>
                Əsas et
              </button>
            )}
            <button type="button" className="btn-small" onClick={() => setForm(null)} disabled={busy}>
              Ləğv et
            </button>
          </div>
        </form>
      )}
      <DataTable
        label="Anbarlar"
        columns={columns}
        rows={rows ?? []}
        rowKey={(w) => w.id}
        onEdit={edit}
        loading={rows === null && !msg}
        error={null}
        empty="Anbar yoxdur."
      />
    </main>
  );
}
